import { CheckboxField, TextAreaField } from '@aws-amplify/ui-react'
import {
  CheckOutlined,
  ContentPasteOutlined,
  DownloadOutlined,
  EditNoteOutlined,
  RouterOutlined,
  UploadOutlined,
} from '@mui/icons-material'
import { Alert, Box, Link, Stack, Tab, Tabs } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomButton from '@/components/inputs/CustomButton'
import { useAlertContext } from '@/contexts/AlertContext'
import { ConfigurationBulkActionTextEditor } from '@/features/controller/components/bulkActions/ConfigurationBulkActionTextEditor'
import { ControllerConfigurationTree } from '@/features/controller/configuration/components/tree/ControllerConfigurationTree'
import { readControllerConfigurationFromClipboard } from '@/features/controller/configuration/models/controllerConfigClipboard'
import type {
  ControllerConfigurationModelUpdate,
  ControllerConfigurationSourceUpdate,
  MultiModalConfiguration,
} from '@/features/controller/configuration/models/controllerConfiguration'
import {
  AppJsonConfigType,
  RaspiPropertiesConfigType,
} from '@/features/controller/configuration/utils/controllerConfigTypes'
import type { ControllerDescription } from '@/features/controller/types'

export interface ConfigurationBulkActionProps {
  applyConfigurationUpdateToSelectedControllers: () => void
  comment: string
  configuration: MultiModalConfiguration
  configurationUpdateInProgress: boolean
  controllers: ControllerDescription[]
  onConfigurationModelUpdated: (update: ControllerConfigurationModelUpdate) => void
  onConfigurationSourceUpdated: (update: ControllerConfigurationSourceUpdate) => void
  setComment: (comment: string) => void
}

export function ConfigurationBulkAction({
  applyConfigurationUpdateToSelectedControllers,
  comment,
  configuration,
  configurationUpdateInProgress,
  controllers,
  onConfigurationModelUpdated,
  onConfigurationSourceUpdated,
  setComment,
}: Readonly<ConfigurationBulkActionProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const [selectedTab, setSelectedTab] = useState('raspi.properties')
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => setConfirmed(false), [configuration])

  const equipmentNumbers = controllers.map((controller) => controller.equipmentNumber).join(', ')

  const commonTabsProps = {
    component: Link,
    iconPosition: 'start',
    style: { minHeight: 0 },
  } as const

  async function handlePasteConfigurationFromClipboard() {
    const clipboardData = await readControllerConfigurationFromClipboard(pushAlert, t)
    if (clipboardData) {
      const { appJsonText, raspiPropertiesText, controllerEquipmentNumber } = clipboardData
      onConfigurationSourceUpdated({
        appJsonText,
        raspiPropertiesText,
        comment: `Copied from controller with equipment number ${controllerEquipmentNumber}`,
      })
      pushAlert({
        message: t('controllers.configuration.tree.controller_actions.paste_config_success', {
          equipmentNumber: controllerEquipmentNumber,
        }),
        severity: 'success',
      })
    }
  }

  if (configurationUpdateInProgress) {
    return (
      <Alert severity="info">
        <Stack alignItems="center" direction="row" gap={1}>
          Please click the
          <CustomButton
            color="primary"
            disabled={true}
            startIcon={<UploadOutlined />}
            variant="contained"
            onClick={() => {}}
          >
            {t('controllers.bulk_actions.configuration_column.save')}
          </CustomButton>
          button for each controller to save the changes.
        </Stack>
      </Alert>
    )
  }

  return (
    <>
      <Alert severity="info" variant="standard">
        <Stack alignItems="center" direction="row" gap={1}>
          To start from an existing configuration, either
          <CustomButton
            color="primary"
            startIcon={<ContentPasteOutlined />}
            variant="contained"
            onClick={() => handlePasteConfigurationFromClipboard()}
          >
            {t('controllers.bulk_actions.replace_configuration.paste')}
          </CustomButton>
          or click
          <CustomButton
            color="secondary"
            disabled={true}
            startIcon={<DownloadOutlined />}
            variant="outlined"
            onClick={() => {}}
          >
            {t('controllers.bulk_actions.configuration_column.load')}
          </CustomButton>
          in the controllers list above.
        </Stack>
      </Alert>
      <Stack border={'2px dashed lightblue'}>
        <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)}>
          <Tab
            icon={<RouterOutlined />}
            label={t('controllers.bulk_actions.replace_configuration.model')}
            value="model"
            {...commonTabsProps}
          />
          <Tab
            icon={<EditNoteOutlined />}
            label={RaspiPropertiesConfigType.shortName}
            value="raspi.properties"
            {...commonTabsProps}
          />
          <Tab icon={<EditNoteOutlined />} label={AppJsonConfigType.shortName} value="app.json" {...commonTabsProps} />
        </Tabs>
        {selectedTab === 'model' && (
          <Box minHeight={600}>
            <ControllerConfigurationTree
              configuration={configuration.model}
              indent={false}
              onConfigurationUpdated={onConfigurationModelUpdated}
            />
          </Box>
        )}
        {selectedTab === 'raspi.properties' && (
          <ConfigurationBulkActionTextEditor
            configType={RaspiPropertiesConfigType}
            value={configuration.source.raspiPropertiesText || ''}
            onApplyChange={(newValue) => {
              onConfigurationSourceUpdated({
                appJsonText: configuration.source.appJsonText,
                raspiPropertiesText: newValue,
              })
            }}
          />
        )}
        {selectedTab === 'app.json' && (
          <ConfigurationBulkActionTextEditor
            configType={AppJsonConfigType}
            value={configuration.source.appJsonText || ''}
            onApplyChange={(newValue) => {
              onConfigurationSourceUpdated({
                appJsonText: newValue,
                raspiPropertiesText: configuration.source.raspiPropertiesText,
              })
            }}
          />
        )}
        <Stack flexDirection="column" gap={2} padding={2}>
          <CheckboxField
            checked={confirmed}
            label={t('controllers.bulk_actions.replace_configuration.confirm', {
              numberOfSelectedControllers: controllers.length,
            })}
            name={'confirm'}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          <CustomTypography>{equipmentNumbers}</CustomTypography>
          <TextAreaField
            label={t('controllers.configuration.comment')}
            placeholder={t('controllers.configuration.comment_placeholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <Box>
            <CustomButton
              color="primary"
              disabled={!confirmed}
              startIcon={<CheckOutlined />}
              variant="contained"
              onClick={() => applyConfigurationUpdateToSelectedControllers()}
            >
              {t('controllers.bulk_actions.replace_configuration.apply')}
            </CustomButton>
          </Box>
        </Stack>
      </Stack>
    </>
  )
}
